/**
 * Group Check Lab rules by insurance policy / coverage line so pickers
 * and the Test Suite are scannable instead of a flat random list.
 *
 * Prefer `applicable_coverage_types`. When that is empty, infer from
 * name/code. Certificate-wide rules (expiration, holder, integrity)
 * stay in "All policies".
 */

export const POLICY_GROUPS = [
  { key: 'GENERAL_LIABILITY', label: 'General Liability', short: 'GL' },
  { key: 'WORKERS_COMP', label: 'Workers Compensation', short: 'WC' },
  { key: 'AUTO_LIABILITY', label: 'Auto Liability', short: 'Auto' },
  { key: 'UMBRELLA', label: 'Umbrella / Excess', short: 'Umbrella' },
  { key: 'PROFESSIONAL_LIABILITY', label: 'Professional / E&O', short: 'E&O' },
  { key: 'PROPERTY', label: 'Property', short: 'Property' },
  { key: 'CYBER', label: 'Cyber', short: 'Cyber' },
  { key: 'BUILDERS_RISK', label: "Builder's Risk", short: 'BR' },
  { key: 'POLLUTION', label: 'Pollution', short: 'Pollution' },
  { key: 'INLAND_MARINE', label: 'Inland Marine', short: 'IM' },
  { key: 'EMPLOYMENT_PRACTICES', label: 'Employment Practices', short: 'EPLI' },
  { key: 'DIRECTORS_OFFICERS', label: 'Directors & Officers', short: 'D&O' },
  { key: 'ALL_POLICIES', label: 'All policies', short: 'All' },
];

const POLICY_BY_KEY = Object.fromEntries(POLICY_GROUPS.map((g) => [g.key, g]));

const CERT_WIDE_PATTERNS = [
  /expir/,
  /effective date/,
  /integrity/,
  /tamper/,
  /certificate holder/,
  /cert holder/,
  /named insured/,
  /acord\s*101/,
  /description of operations/,
];

const INFER_PATTERNS = [
  { key: 'GENERAL_LIABILITY', tests: [/\bgl\b/, /\bcgl\b/, /general liability/, /commercial general/] },
  { key: 'WORKERS_COMP', tests: [/\bwc\b/, /workers.?comp/, /workmen.?s?.?comp/, /employer.?s? liability/] },
  { key: 'AUTO_LIABILITY', tests: [/\bauto\b/, /\bal\b/, /hired.?\/?non.?owned/, /business auto/, /commercial auto/] },
  { key: 'UMBRELLA', tests: [/umbrella/, /\bexcess\b/, /\bcup\b/] },
  { key: 'PROFESSIONAL_LIABILITY', tests: [/professional/, /\be\s*&\s*o\b/, /errors and omissions/, /malpractice/] },
  { key: 'PROPERTY', tests: [/\bproperty\b/] },
  { key: 'CYBER', tests: [/\bcyber\b/] },
  { key: 'BUILDERS_RISK', tests: [/builders?.?\s*risk/] },
  { key: 'POLLUTION', tests: [/pollution/, /environmental/] },
  { key: 'INLAND_MARINE', tests: [/inland marine/] },
  { key: 'EMPLOYMENT_PRACTICES', tests: [/\bepli\b/, /employment practices/] },
  { key: 'DIRECTORS_OFFICERS', tests: [/\bd\s*&\s*o\b/, /directors and officers/] },
];

function coverageTypesOf(req) {
  const types = req?.applicable_coverage_types;
  return Array.isArray(types) ? types.filter(Boolean) : [];
}

function haystack(req) {
  return `${req?.name || ''} ${req?.code || ''} ${req?.description || ''}`.toLowerCase();
}

/**
 * @param {object} req
 * @returns {string} POLICY_GROUPS key
 */
export function policyKeyForRequirement(req) {
  const types = coverageTypesOf(req);
  if (types.length) {
    const known = POLICY_GROUPS.find((g) => g.key !== 'ALL_POLICIES' && types.includes(g.key));
    return known?.key || types[0];
  }

  const text = haystack(req);
  if (CERT_WIDE_PATTERNS.some((re) => re.test(text))) {
    return 'ALL_POLICIES';
  }
  const inferred = INFER_PATTERNS.find((entry) => entry.tests.some((re) => re.test(text)));
  return inferred?.key || 'ALL_POLICIES';
}

/**
 * @param {object} req
 * @returns {{ key: string, label: string, short: string }}
 */
export function policyMetaForRequirement(req) {
  const key = policyKeyForRequirement(req);
  return POLICY_BY_KEY[key] || { key, label: key.replace(/_/g, ' '), short: key };
}

/**
 * Human label for chips. Multiple assigned coverage types are joined.
 * @param {object} req
 * @returns {string}
 */
export function policyChipLabel(req) {
  const types = coverageTypesOf(req);
  if (types.length > 1) {
    return types
      .map((t) => POLICY_BY_KEY[t]?.short || t)
      .join(' + ');
  }
  return policyMetaForRequirement(req).short;
}

function sortWithinGroup(a, b) {
  const orderA = Number.isFinite(a.order) ? a.order : 9999;
  const orderB = Number.isFinite(b.order) ? b.order : 9999;
  if (orderA !== orderB) return orderA - orderB;
  return String(a.name || '').localeCompare(String(b.name || ''));
}

/**
 * @param {Array<object>} requirements
 * @returns {Array<{ key: string, label: string, short: string, items: object[] }>}
 */
export function groupRequirementsByPolicy(requirements) {
  const list = Array.isArray(requirements) ? requirements : [];
  const buckets = new Map();

  list.forEach((req) => {
    const meta = policyMetaForRequirement(req);
    if (!buckets.has(meta.key)) {
      buckets.set(meta.key, { ...meta, items: [] });
    }
    buckets.get(meta.key).items.push(req);
  });

  buckets.forEach((group) => {
    group.items.sort(sortWithinGroup);
  });

  const ordered = [];
  POLICY_GROUPS.forEach((g) => {
    const group = buckets.get(g.key);
    if (group?.items.length) ordered.push(group);
    buckets.delete(g.key);
  });
  buckets.forEach((group) => {
    if (group.items.length) ordered.push(group);
  });
  return ordered;
}
